<template>
  <div>
    <div
      class="group-row"
      :class="{
        highlighted: isHighlighted,
        expanded: expanded
      }"
      :style="{ paddingLeft: `${16 + level * 24}px` }"
    >
      <div class="group-name-col">
        <div class="d-flex align-center">
          <v-btn v-if="hasChildren" icon x-small class="expand-btn mr-1" @click="expanded = !expanded">
            <v-icon x-small>{{ expanded ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
          </v-btn>
          <div v-else class="expand-placeholder"></div>

          <div class="group-count" v-if="childCount > 0">{{ childCount }}</div>
          <div class="group-name">{{ group.fullName || group.shortName }}</div>
        </div>
      </div>

      <div class="group-type-col">
        <v-chip v-if="group.type" small :color="getTypeColor(group.type)" text-color="white" class="type-badge">
          {{ $i18n.t(capitalize(group.type)) }}
        </v-chip>
        <span v-else class="no-type">-</span>
      </div>

      <div class="group-client-col">
        <div v-if="clientName" class="client-name">
          {{ clientName }}
        </div>
        <div v-else class="no-client">-</div>
      </div>

      <div class="group-contract-col">
        <div v-if="contractName" class="contract-name">
          {{ contractName }}
        </div>
        <div v-else class="no-contract">-</div>
      </div>

      <div class="group-users-col">
        <div class="user-avatars">
          <template v-if="group.users && group.users.length > 0">
            <div class="d-flex align-center">
              <v-icon small class="mr-1">people</v-icon>
              {{ group.users ? group.users.length : 0 }}
            </div>
            <div v-if="group.users.length > 3" class="more-users">+{{ group.users.length - 3 }}</div>
          </template>
          <div v-else class="no-users">-</div>
        </div>
      </div>

      <div class="group-actions-col">
        <v-menu bottom left>
          <template v-slot:activator="{ on }">
            <v-btn icon small v-on="on" class="action-btn">
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-tile @click="$emit('view', group)">
              <v-list-tile-title>
                <v-icon small left>mdi-eye</v-icon>
                {{ $i18n.t("View Details") }}
              </v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="$emit('edit', group)">
              <v-list-tile-title>
                <v-icon small left>mdi-pencil</v-icon>
                {{ $i18n.t("Edit Group") }}
              </v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="$emit('manage-users', group)">
              <v-list-tile-title>
                <v-icon small left>mdi-account-multiple</v-icon>
                {{ $i18n.t("Manage Users") }}
              </v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </div>
    </div>

    <div v-if="expanded && hasChildren" class="child-groups">
      <group-tree-item
        v-for="childGroup in childGroups"
        :key="childGroup._id"
        :group="childGroup"
        :all-groups="allGroups"
        :search-term="searchTerm"
        :level="level + 1"
        @view="$emit('view', $event)"
        @edit="$emit('edit', $event)"
        @manage-users="$emit('manage-users', $event)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { GROUP_TYPE_COLOR } from "../../constants";

export default {
  name: "GroupTreeItem",
  props: {
    group: {
      type: Object,
      required: true
    },
    allGroups: {
      type: Array,
      required: true
    },
    searchTerm: {
      type: String,
      default: ""
    },
    level: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      expanded: false
    };
  },
  computed: {
    ...mapGetters({
      users: "users/getUsers",
      clients: "client/getClients",
      contracts: "contract/getContractsMeta"
    }),
    childGroups() {
      return this.allGroups.filter(g => g.parent === this.group._id);
    },
    hasChildren() {
      return this.childGroups.length > 0;
    },
    childCount() {
      return this.childGroups.length;
    },
    isHighlighted() {
      if (!this.searchTerm) return false;

      return (
        (this.group.shortName && this.group.shortName.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
        (this.group.fullName && this.group.fullName.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
        (this.group.type && this.group.type.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
        (this.clientName && this.clientName.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
        (this.contractName && this.contractName.toLowerCase().includes(this.searchTerm.toLowerCase()))
      );
    },
    clientName() {
      if (!this.group.client) return null;
      const client = this.clients.find(c => c._id === this.group.client);
      return client ? client.name : null;
    },
    contractName() {
      if (!this.group.contract) return null;
      const contract = this.contracts.find(c => c._id === this.group.contract);
      return contract ? contract.name : null;
    }
  },
  watch: {
    searchTerm: {
      handler(newVal) {
        // Auto-expand if this group or any of its children match the search
        if (newVal && this.groupOrChildrenMatch(this.group, newVal.toLowerCase())) {
          this.expanded = true;
        } else if (!newVal) {
          // Collapse when search is cleared
          this.expanded = false;
        }
      },
      immediate: true
    }
  },
  methods: {
    getTypeColor(type) {
      return GROUP_TYPE_COLOR[type] || "grey";
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    getUserName(userId) {
      const user = this.users.find(u => u._id === userId);
      return user ? user.name : userId;
    },
    groupOrChildrenMatch(group, searchTerm) {
      // Check if the current group matches
      if (
        (group.shortName && group.shortName.toLowerCase().includes(searchTerm)) ||
        (group.fullName && group.fullName.toLowerCase().includes(searchTerm)) ||
        (group.type && group.type.toLowerCase().includes(searchTerm))
      ) {
        return true;
      }

      // Check if client matches
      const client = this.clients.find(c => c._id === group.client);
      if (client && client.name.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Check if contract matches
      const contract = this.contracts.find(c => c._id === group.contract);
      if (contract && contract.name.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Check if any children match
      return this.childGroups.some(child => this.groupOrChildrenMatch(child, searchTerm));
    }
  }
};
</script>

<style scoped>
.group-row {
  display: flex;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
  align-items: center;
}

.group-row:hover {
  background-color: #f9f9f9;
}

.highlighted {
  background-color: rgba(255, 251, 230, 0.6);
}

.expanded {
  background-color: #f5f7f9;
}

.expand-btn {
  margin-right: 8px;
  width: 20px;
  height: 20px;
}

.expand-placeholder {
  width: 20px;
  margin-right: 8px;
}

.group-count {
  background-color: #e0e0e0;
  color: #555;
  border-radius: 12px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 500;
  margin-right: 8px;
  min-width: 20px;
  text-align: center;
}

.group-name {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.group-name-col {
  flex: 3;
  padding-right: 16px;
  display: flex;
  align-items: center;
}

.group-type-col {
  flex: 1;
  min-width: 100px;
  padding-right: 16px;
}

.group-client-col {
  flex: 1;
  min-width: 120px;
  padding-right: 16px;
}

.group-contract-col {
  flex: 1;
  min-width: 120px;
  padding-right: 16px;
}

.group-users-col {
  flex: 1;
  min-width: 100px;
  padding-right: 16px;
}

.group-actions-col {
  flex: 0 0 80px;
  display: flex;
  justify-content: flex-end;
}

.type-badge {
  font-size: 12px;
  height: 24px !important;
}

.no-type,
.no-client,
.no-contract,
.no-users {
  color: #999;
  font-style: italic;
  font-size: 13px;
}

.client-name,
.contract-name {
  font-size: 13px;
  color: #333;
}

.user-avatars {
  display: flex;
  align-items: center;
}

.user-avatar {
  margin-right: 4px;
  background-color: #e0e0e0;
}

.more-users {
  background-color: #e0e0e0;
  color: #555;
  border-radius: 12px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 500;
}

.child-groups {
  border-left: 1px solid #e0e0e0;
  margin-left: 20px;
}
</style>
